/**
 * quoteApi.js - the quote builder's calls to the server (routes/quotes.js,
 * routes/quoteRates.js). Each answers { ok, status, body } (JSON) or
 * { ok, status, blob, name } (a document), never throws.
 *
 * Every call takes the API base its screen is in (useApiBase(), '/api'
 * normally): on the GLOBAL KSA tab the same calls reach KSA's own builder and
 * rate card (Saudi Riyals), so which card prices a quote follows the tab the
 * person is on, never anything they type.
 */
import { json, send } from '../../utils/api';

const post = (body) => send('POST', body);

export const getRates = (base = '/api') => json(`${base}/quote-rates`);
export const saveRates = (settings, updatedAt, base = '/api') => json(`${base}/quote-rates`, send('PUT', { settings, updatedAt }));
export const addPart = (part, base = '/api') => json(`${base}/quote-rates/catalog`, post(part));
export const savePart = (id, part, base = '/api') => json(`${base}/quote-rates/catalog/${encodeURIComponent(id)}`, send('PUT', part));
export const deletePart = (id, base = '/api') => json(`${base}/quote-rates/catalog/${encodeURIComponent(id)}`, { method: 'DELETE' });

// the builder's settings: the full card with Quotes Edit, else names and wording only
export const getQuoteSettings = (base = '/api') => json(`${base}/quotes/settings`);
export const getCapabilities = (base = '/api') => json(`${base}/quotes/capabilities`);
// the Worksheet quote's people: names, levels, types, locations (never pay) and { seesCosts }
export const getQuoteStaff = (base = '/api') => json(`${base}/quotes/staff`);
export const getDrafts = (projectId, base = '/api') => json(`${base}/quotes/drafts/${encodeURIComponent(projectId)}`);
export const saveDraft = (projectId, type, revision, data, base = '/api') =>
  json(`${base}/quotes/drafts/${encodeURIComponent(projectId)}`, send('PUT', { type, revision, data }));
export const calcQuote = (type, data, base = '/api') => json(`${base}/quotes/calc`, post({ type, data }));

/** The file name out of a Content-Disposition header. */
export function fileNameFrom(header, fallback) {
  const h = String(header || '');
  const star = /filename\*=UTF-8''([^;]+)/i.exec(h);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(h);
  return plain ? plain[1] : fallback;
}

/** POST {base}/quotes/generate or /pdf -> { ok, blob, name } or { ok: false, status, error }. */
export async function fetchDocument(kind, type, data, base = '/api') {
  try {
    const r = await fetch(`${base}/quotes/${kind === 'pdf' ? 'pdf' : 'generate'}`, post({ type, data }));
    if (!r.ok) {
      const body = await r.json().catch(() => null);
      return { ok: false, status: r.status, error: (body && body.error) || `The server said ${r.status}.` };
    }
    const blob = await r.blob();
    return {
      ok: true,
      status: r.status,
      blob,
      name: fileNameFrom(r.headers.get('content-disposition'), `quote.${kind === 'pdf' ? 'pdf' : 'docx'}`),
    };
  } catch {
    return { ok: false, status: 0, error: 'Could not reach the server.' };
  }
}
